import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Copy,
  ExternalLink,
  Radio,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Link2,
  Unlink,
  ChevronsUpDown,
  Check,
  UserMinus,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';

import { SimStatusBadge } from './SimStatusBadge';
import {
  useSimCard,
  useSimTransmitter,
  useSimSubscriptions,
  useMatchCandidates,
  useLinkSimCardTransmitter,
  useAssignSimCardAccount,
} from '../hooks/useSimCards';
import { useAccountsForSelect } from '@/features/subscriptions/hooks';
import type { VolumeTier } from '../utils/pricing';
import { getSimMonthlyCost, formatCost } from '../utils/pricing';
import type { SimSubscription, MatchConfidence } from '../types';

// =============================================================================
// Props
// =============================================================================

interface SimDetailSheetProps {
  simCardId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volumeTier?: VolumeTier;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDataSize(mb: number): string {
  if (mb < 1) {
    return `${(mb * 1024).toFixed(1)} KB`;
  }
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(2)} GB`;
  }
  return `${mb.toFixed(2)} MB`;
}

function copyToClipboard(value: string, label: string) {
  navigator.clipboard.writeText(value);
  toast.success(`${label} copied to clipboard`);
}

// =============================================================================
// Sub-components
// =============================================================================

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5 border-b last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%] break-words">{value || '-'}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </h4>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function CopyableValue({ value, label }: { value: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-mono text-xs">{value}</span>
      <button
        type="button"
        onClick={() => copyToClipboard(value, label)}
        className="text-muted-foreground hover:text-foreground transition-colors"
        title={`Copy ${label}`}
      >
        <Copy className="h-3 w-3" />
      </button>
    </span>
  );
}

function SubscriptionStatusBadge({ status }: { status: SimSubscription['status'] }) {
  const config: Record<SimSubscription['status'], { label: string; className: string }> = {
    active: { label: 'Active', className: 'bg-green-100 text-green-700' },
    expired: { label: 'Expired', className: 'bg-red-100 text-red-700' },
    draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
    cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-600' },
    pending_renewal: { label: 'Pending Renewal', className: 'bg-yellow-100 text-yellow-700' },
  };

  const cfg = config[status] || { label: status, className: 'bg-gray-100 text-gray-600' };

  return (
    <Badge className={cfg.className}>
      {cfg.label}
    </Badge>
  );
}

function ConfidenceBadge({ confidence }: { confidence: MatchConfidence }) {
  const config: Record<MatchConfidence, { className: string }> = {
    high: { className: 'bg-green-100 text-green-700' },
    medium: { className: 'bg-yellow-100 text-yellow-700' },
    low: { className: 'bg-gray-100 text-gray-600' },
  };

  return (
    <Badge className={config[confidence].className}>
      {confidence}
    </Badge>
  );
}

function LoadingSkeleton() {
  return (
    <div className="mt-6 space-y-6">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-3/4" />
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function SimDetailSheet({
  simCardId,
  open,
  onOpenChange,
  volumeTier = 'tier2',
}: SimDetailSheetProps) {
  const { data: sim, isLoading, error } = useSimCard(simCardId ?? '');
  const { data: transmitter } = useSimTransmitter(simCardId ?? '');
  const { data: subscriptionInfo } = useSimSubscriptions(simCardId ?? '');
  const { data: candidates } = useMatchCandidates(simCardId ?? '');
  const linkMutation = useLinkSimCardTransmitter();
  const assignMutation = useAssignSimCardAccount();

  const [selectedCandidateId, setSelectedCandidateId] = useState<string>('');

  // Account assignment state
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const debouncedAccountSearch = useDebounce(accountSearch, 300);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);

  const { data: accounts = [] } = useAccountsForSelect(debouncedAccountSearch);

  const isLinked = !!sim?.mongo_transmitter_id;

  function handleLink() {
    if (!simCardId || !selectedCandidateId) return;
    const candidate = candidates?.find((c) => c.transmitter_id === selectedCandidateId);
    linkMutation.mutate(
      {
        simCardId,
        input: {
          transmitter_id: selectedCandidateId,
          match_method: candidate?.match_method || 'manual',
        },
      },
      { onSuccess: () => setSelectedCandidateId('') }
    );
  }

  function handleUnlink() {
    if (!simCardId) return;
    linkMutation.mutate({
      simCardId,
      input: { transmitter_id: null, match_method: 'manual' },
    });
  }

  function handleAssignAccount(accountId: string) {
    if (!simCardId) return;
    assignMutation.mutate(
      { simCardId, input: { account_id: accountId } },
      {
        onSuccess: () => {
          setAccountSearchOpen(false);
          setAccountSearch('');
        },
      }
    );
  }

  function handleUnassignAccount() {
    if (!simCardId) return;
    assignMutation.mutate(
      { simCardId, input: { account_id: null } },
      { onSuccess: () => setShowUnassignConfirm(false) }
    );
  }

  const monthlyCost = sim ? getSimMonthlyCost(sim.rate_plan, sim.status, volumeTier) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : sim ? (
              <>
                <span className="truncate font-mono text-sm">{sim.iccid}</span>
                <SimStatusBadge status={sim.status} />
              </>
            ) : (
              <span>SIM Card Details</span>
            )}
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="mt-6 text-center py-8">
            <p className="text-red-500 text-sm">Failed to load SIM card details.</p>
          </div>
        ) : sim ? (
          <div className="mt-6 space-y-5">
            {/* ------------------------------------------------------------------ */}
            {/* Identity */}
            {/* ------------------------------------------------------------------ */}
            <Section title="Identity">
              <DetailRow
                label="ICCID"
                value={<CopyableValue value={sim.iccid} label="ICCID" />}
              />
              <DetailRow
                label="IMEI"
                value={
                  sim.imei ? <CopyableValue value={sim.imei} label="IMEI" /> : '-'
                }
              />
              <DetailRow
                label="MSISDN"
                value={
                  sim.msisdn ? <CopyableValue value={sim.msisdn} label="MSISDN" /> : '-'
                }
              />
              <DetailRow label="IMSI" value={sim.imsi} />
              <DetailRow label="Jasper Device ID" value={sim.jasper_device_id} />
              <DetailRow label="Jasper Account ID" value={sim.jasper_account_id} />
            </Section>

            {/* ------------------------------------------------------------------ */}
            {/* Status & Lifecycle */}
            {/* ------------------------------------------------------------------ */}
            <Section title="Status & Lifecycle">
              <DetailRow
                label="Status"
                value={<SimStatusBadge status={sim.status} />}
              />
              {sim.jasper_status && sim.jasper_status !== sim.status && (
                <DetailRow label="Jasper Raw Status" value={sim.jasper_status} />
              )}
              <DetailRow label="Date Activated" value={formatDate(sim.date_activated)} />
              <DetailRow label="Date Shipped" value={formatDate(sim.date_shipped)} />
            </Section>

            {/* ------------------------------------------------------------------ */}
            {/* Account & Contract */}
            {/* ------------------------------------------------------------------ */}
            <Section title="Account & Subscription">
              {sim.account_id && sim.account_name ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <a
                      href={`/accounts/${sim.account_id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                    >
                      {sim.account_name}
                      {sim.account_number && (
                        <span className="text-muted-foreground">({sim.account_number})</span>
                      )}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <div className="flex items-center gap-1">
                      <Popover open={accountSearchOpen} onOpenChange={(open) => { setAccountSearchOpen(open); if (!open) setAccountSearch(''); }}>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                            Change
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0" align="end" onWheel={(e) => e.stopPropagation()}>
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder="Search accounts..."
                              value={accountSearch}
                              onValueChange={setAccountSearch}
                            />
                            <CommandList>
                              <CommandEmpty>No accounts found</CommandEmpty>
                              <CommandGroup>
                                {accounts.map((account) => (
                                  <CommandItem
                                    key={account.id}
                                    value={account.id}
                                    onSelect={() => handleAssignAccount(account.id)}
                                    disabled={assignMutation.isPending}
                                  >
                                    <Check className={cn('mr-2 h-4 w-4', account.id === sim.account_id ? 'opacity-100' : 'opacity-0')} />
                                    <div className="flex flex-col">
                                      <span className="text-sm">{account.name}</span>
                                      <span className="text-xs text-muted-foreground">{account.accountNumber}</span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
                        onClick={() => setShowUnassignConfirm(true)}
                        disabled={assignMutation.isPending}
                      >
                        <UserMinus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3">
                    <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-orange-700">
                      No account assigned to this SIM card.
                    </span>
                  </div>
                  <Popover open={accountSearchOpen} onOpenChange={(open) => { setAccountSearchOpen(open); if (!open) setAccountSearch(''); }}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        role="combobox"
                        aria-expanded={accountSearchOpen}
                        className="w-full justify-between"
                      >
                        Assign Account
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search accounts..."
                          value={accountSearch}
                          onValueChange={setAccountSearch}
                        />
                        <CommandList>
                          <CommandEmpty>No accounts found</CommandEmpty>
                          <CommandGroup>
                            {accounts.map((account) => (
                              <CommandItem
                                key={account.id}
                                value={account.id}
                                onSelect={() => handleAssignAccount(account.id)}
                                disabled={assignMutation.isPending}
                              >
                                <div className="flex flex-col">
                                  <span className="text-sm">{account.name}</span>
                                  <span className="text-xs text-muted-foreground">{account.accountNumber}</span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {subscriptionInfo && subscriptionInfo.contracts.length > 0 ? (
                <div className="space-y-2 mt-2">
                  {subscriptionInfo.contracts.map((contract) => (
                    <div
                      key={contract.id}
                      className="rounded-md border p-3 space-y-1.5"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium">{contract.contract_number}</span>
                        </div>
                        <SubscriptionStatusBadge status={contract.status} />
                      </div>
                      {contract.name && (
                        <p className="text-xs text-muted-foreground">{contract.name}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {contract.start_date && (
                          <span>{formatDate(contract.start_date)}</span>
                        )}
                        {contract.start_date && contract.end_date && <span>-</span>}
                        {contract.end_date && (
                          <span>{formatDate(contract.end_date)}</span>
                        )}
                      </div>
                      {contract.total_value !== null && (
                        <p className="text-xs font-medium">
                          ${contract.total_value.toLocaleString('en-CA', { minimumFractionDigits: 2 })} {contract.currency}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : sim.account_id ? (
                <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 mt-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-orange-700">
                    No subscriptions found for this account.
                  </span>
                </div>
              ) : null}
            </Section>

            {/* Unassign Confirmation Dialog */}
            <AlertDialog open={showUnassignConfirm} onOpenChange={setShowUnassignConfirm}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Unassign Account</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove <strong>{sim.account_name}</strong> from SIM card <strong className="font-mono">{sim.iccid}</strong>. The SIM card will become unassigned.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleUnassignAccount}
                    className="bg-red-600 hover:bg-red-700"
                    disabled={assignMutation.isPending}
                  >
                    {assignMutation.isPending ? 'Unassigning...' : 'Unassign'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* ------------------------------------------------------------------ */}
            {/* Gateway / Transmitter */}
            {/* ------------------------------------------------------------------ */}
            <Section title="Gateway / Transmitter">
              {isLinked && transmitter ? (
                <div className="space-y-2">
                  <DetailRow
                    label="Name"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <Radio className="h-3.5 w-3.5 text-muted-foreground" />
                        {transmitter.name || '-'}
                      </span>
                    }
                  />
                  <DetailRow label="Serial" value={transmitter.serial} />
                  <DetailRow label="Model" value={transmitter.model} />
                  <DetailRow label="Manufacturer" value={transmitter.manufacturer} />
                  <DetailRow label="Account" value={transmitter.organisation} />
                  <DetailRow
                    label="Last Packet"
                    value={
                      transmitter.last_packet_at
                        ? formatDistanceToNow(new Date(transmitter.last_packet_at), { addSuffix: true })
                        : '-'
                    }
                  />
                  <DetailRow label="Match Method" value={sim.match_method} />
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 text-red-600 hover:text-red-700"
                    onClick={handleUnlink}
                    disabled={linkMutation.isPending}
                  >
                    <Unlink className="h-3.5 w-3.5 mr-1.5" />
                    Unlink Transmitter
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    No transmitter linked to this SIM card.
                  </p>
                  {candidates && candidates.length > 0 ? (
                    <div className="space-y-2">
                      <Select
                        value={selectedCandidateId}
                        onValueChange={setSelectedCandidateId}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a transmitter..." />
                        </SelectTrigger>
                        <SelectContent>
                          {candidates.map((c) => (
                            <SelectItem key={c.transmitter_id} value={c.transmitter_id}>
                              <span className="flex items-center gap-2">
                                {c.transmitter_name}
                                <ConfidenceBadge confidence={c.confidence} />
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        onClick={handleLink}
                        disabled={!selectedCandidateId || linkMutation.isPending}
                      >
                        <Link2 className="h-3.5 w-3.5 mr-1.5" />
                        Link Transmitter
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      No match candidates available.
                    </p>
                  )}
                </div>
              )}
            </Section>

            {/* ------------------------------------------------------------------ */}
            {/* Usage & Billing */}
            {/* ------------------------------------------------------------------ */}
            <Section title="Usage & Billing">
              <DetailRow label="Rate Plan" value={sim.rate_plan} />
              <DetailRow label="Communication Plan" value={sim.communication_plan} />
              <DetailRow label="Data Usage (CTD)" value={formatDataSize(sim.usage_ctd_mb)} />
              <DetailRow label="SMS Count (CTD)" value={sim.sms_usage_ctd.toLocaleString()} />
              <DetailRow label="Session Count" value={sim.data_session_count.toLocaleString()} />
              <DetailRow
                label="Est. Monthly Cost"
                value={formatCost(monthlyCost)}
              />
            </Section>

            {/* ------------------------------------------------------------------ */}
            {/* Session */}
            {/* ------------------------------------------------------------------ */}
            {(sim.last_session_ip || sim.last_session_start || sim.last_session_end) && (
              <Section title="Session">
                <DetailRow label="Last IP" value={sim.last_session_ip} />
                <DetailRow label="Session Start" value={formatDate(sim.last_session_start)} />
                <DetailRow label="Session End" value={formatDate(sim.last_session_end)} />
              </Section>
            )}

            {/* ------------------------------------------------------------------ */}
            {/* Sync */}
            {/* ------------------------------------------------------------------ */}
            <Section title="Sync">
              <DetailRow
                label="Last Sync"
                value={
                  sim.last_sync_at
                    ? formatDistanceToNow(new Date(sim.last_sync_at), { addSuffix: true })
                    : '-'
                }
              />
              <DetailRow
                label="Sync Status"
                value={
                  sim.sync_status ? (
                    <span className="inline-flex items-center gap-1.5">
                      {sim.sync_status === 'success' ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                      ) : sim.sync_status === 'error' ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                      ) : null}
                      {sim.sync_status}
                    </span>
                  ) : (
                    '-'
                  )
                }
              />
              {sim.sync_error && (
                <DetailRow
                  label="Sync Error"
                  value={
                    <span className="text-red-600 text-xs">{sim.sync_error}</span>
                  }
                />
              )}
            </Section>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
